import { useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

interface LanguageDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LanguageDropdown({ isOpen, onClose }: LanguageDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Try to use the language context, but handle the case where it's not available
  let languageContext;
  try {
    languageContext = useLanguage();
  } catch (error) {
    // If the provider is not available, render nothing
    return null;
  }

  const { currentLanguage, setLanguage, availableLanguages, isInitialized } = languageContext;

  const handleLanguageSelect = (languageCode: string) => {
    console.log('Language changed to:', languageCode); // Debug log
    setLanguage(languageCode as any);
    onClose();
  };

  // Function to get the correct flag file name
  const getFlagFileName = (languageCode: string) => {
    const flagMap: { [key: string]: string } = {
      'uk': 'uk_flag.svg',
      'us': 'us_flag.svg',
      'ae': 'uae_flag.svg',
      'de': 'germany_flag.svg',
      'iq': 'iraq_flag.svg'
    };
    return flagMap[languageCode] || `${languageCode}_flag.svg`;
  };

  // Don't render if not initialized or not open
  if (!isInitialized || !isOpen) return null;

  return (
    <div 
      ref={dropdownRef}
      className="fixed z-[9999] bg-white rounded-lg shadow-xl border border-gray-200"
      style={{
        top: '160px',
        right: '100px',
        width: '200px'
      }}
    >
      <div className="py-2">
        {availableLanguages.map((language) => (
          <button
            key={language.code}
            onClick={() => handleLanguageSelect(language.code)}
            className={`w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 ${
              currentLanguage === language.code ? 'bg-black text-white' : 'text-gray-700'
            }`}
          >
            <img
              src={`/img/${getFlagFileName(language.code)}`}
              alt={`${language.name} flag`}
              className="w-6 h-4 object-cover rounded"
            />
            <span className="text-sm font-medium">{language.name}</span>
            {currentLanguage === language.code && (
              <div className="ml-auto w-2 h-2 bg-white rounded-full"></div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
